import React, {useEffect, useRef, useState} from 'react';
import {ServiceResource, useAuth} from '../hooks/use-auth';
import {Notification} from '../navigation/page-layout';
import {CommonBridgeDataRequest, useInboundBridgeFilterData} from '../hooks/use-inbound-bridge-filter-data';
import {translateErrorToReactNode} from '../common';
import {Box, Container, ContentLayout, Header, SpaceBetween, Spinner} from '@amzn/awsui-components-react';
import {BridgeType, InboundBridgeChannelPlanActual} from './index';
import {InboundBridgeFilters} from './inbound-bridge-filters';
import {useRetrieveInboundBridgePreset} from '../hooks/use-analytics-api';
import '../../assets/styles/inbound-bridging-styles.scss';
import {InboundBridgeChannelPlanActualTable} from './inbound-bridge-channel-plan-actual-table';

const COMPONENT_TEXTS = {
    page_title: 'Channel Breakdown',
    page_description: 'Plan to actuals bridging by channel',
} as const;

export function InboundBridgeP2AChannels(props: {pushNotification: (notification: Notification) => void}) {
    function createErrorListener(header: string) {
        return (e: any) => {
            props.pushNotification({
                type: 'error',
                content: translateErrorToReactNode(e),
                header,
            });
        };
    }

    const auth = useAuth();
    const [channelPlanActualDaily, setChannelPlanActualDaily] = useState<InboundBridgeChannelPlanActual[]>([]);
    const isLoadingDaily = useRef<boolean>(true);

    const tenant = {
        business: auth.authInformation!.current!.businessId,
        country: auth.authInformation!.current!.country,
    };

    const clientConfiguration = auth.authInformation!.getCurrentServiceEndpoint(ServiceResource.ExplainabilityView);

    const {
        execute: executeRetrieveDaily,
        status: retrieveDailyStatus,
        value: retrieveDailyResponse,
    } = useRetrieveInboundBridgePreset(clientConfiguration, createErrorListener('RetrieveDailyChannelPlanActual failed'), [auth]);

    const {nodeFilterDataMap, selectedFilter, setSelectedFilter, isLoadingFilterData} = useInboundBridgeFilterData(
        BridgeType.P2A,
        tenant,
        false,
        clientConfiguration,
        createErrorListener,
        (params: CommonBridgeDataRequest) => {
            executeRetrieveDaily({
                ...tenant,
                forecastType: 'sop',
                bridgeType: BridgeType.P2A,
                timeGranularity: 'DAY',
                nodeGranularity: 'NODE',
                preset: 'CHANNEL_PLAN_ACTUAL',
                filter: JSON.stringify({
                    nodes: params.nodes,
                    balanceDateStart: params.balanceDateStart,
                    balanceDateEnd: params.balanceDateEnd,
                    fromHorizonInWeeks: params.fromHorizonInWeeks,
                    toHorizonInWeeks: params.toHorizonInWeeks,
                }),
            });
        }
    );

    isLoadingDaily.current = ['idle', 'pending'].includes(retrieveDailyStatus);

    useEffect(() => {
        if (!isLoadingDaily.current && retrieveDailyResponse?.records) {
            setChannelPlanActualDaily(
                retrieveDailyResponse.records.map(
                    (record) =>
                        ({
                            node: 'ALL',
                            date: record.balanceDate,
                            channel: record.granularity['channel'],
                            name: record.name,
                            value: record.value,
                        } as InboundBridgeChannelPlanActual)
                )
            );
        }
    }, [isLoadingDaily.current]);

    return (
        <ContentLayout defaultPadding>
            <SpaceBetween direction="vertical" size="xxl">
                <Header variant="h2" description={COMPONENT_TEXTS.page_description}>
                    {COMPONENT_TEXTS.page_title}
                </Header>
                <InboundBridgeFilters
                    bridgeType={BridgeType.P2A}
                    tenant={tenant}
                    hideNodeSelect={false}
                    multiSelectNode={true}
                    nodeFilterDataMap={nodeFilterDataMap}
                    selectedFilter={selectedFilter}
                    setSelectedFilter={setSelectedFilter}
                    isLoadingFilterData={isLoadingFilterData}
                    isLoadingData={isLoadingDaily.current}
                    onApply={() => {
                        if (selectedFilter) {
                            const nodes = selectedFilter.nodes?.length ? selectedFilter.nodes : ['ALL'];
                            isLoadingDaily.current = true;
                            executeRetrieveDaily({
                                ...tenant,
                                forecastType: 'sop',
                                bridgeType: BridgeType.P2A,
                                timeGranularity: 'DAY',
                                nodeGranularity: 'NODE',
                                preset: 'CHANNEL_PLAN_ACTUAL',
                                filter: JSON.stringify({
                                    nodes,
                                    balanceDateStart: selectedFilter.balanceDateStart,
                                    balanceDateEnd: selectedFilter.balanceDateEnd,
                                    fromHorizonInWeeks: selectedFilter.fromHorizonInWeeks,
                                    toHorizonInWeeks: selectedFilter.toHorizonInWeeks,
                                    channels: selectedFilter.channels,
                                    ibTypes: selectedFilter.ibTypes,
                                    tempZones: selectedFilter.tempZones,
                                }),
                            });
                        }
                    }}
                />
                {renderVisualizations()}
            </SpaceBetween>
        </ContentLayout>
    );

    function renderVisualizations(): React.ReactElement {
        if (isLoadingFilterData) {
            return (
                <Box textAlign="center" color="inherit">
                    <Spinner size="large" />
                </Box>
            );
        }

        return (
            <Container header={<Header variant="h2">Channel Plan vs Actual</Header>}>
                <InboundBridgeChannelPlanActualTable
                    bridgeData={channelPlanActualDaily}
                    selectedFilter={selectedFilter}
                    isLoading={isLoadingDaily.current}
                />
            </Container>
        );
    }
}
